// authServiceUtil.js
import { signInAnonymously } from 'firebase/auth';
import { auth } from '../firebase'; // Ensure this points to your firebase config file
import { ADMIN_IDLE_TIMEOUT_MS, SUPER_ADMIN_EMAILS } from '../constants/bookingConst';

/**
 * Normalizes an email for comparison (trimmed, lower-cased). Firebase
 * echoes back whatever casing the account was created with, so raw
 * equality checks against a stored value are unreliable.
 * @param {string} email
 * @returns {string}
 */
const normalizeEmail = (email) => (email || '').trim().toLowerCase();

/**
 * True for a super-admin account (SUPER_ADMIN_EMAILS in bookingConst.js) —
 * NOT a test for "is this an admin" or "is this the ambient guest session".
 * Every guest is signed in anonymously (see initializeDefaultUser), so an
 * anonymous user has no email and never matches this. Used for the rights
 * only super-admins have: saving /config (mirrored in database.rules.json)
 * and editing past bookings (AdminBooking.jsx's isOwnerAccount). For "is the
 * current Firebase user just the anonymous guest session", use
 * isAnonymousUser below instead.
 * @param {string} email
 * @returns {boolean}
 */
export const isSuperAdmin = (email) => SUPER_ADMIN_EMAILS
  .map(normalizeEmail)
  .includes(normalizeEmail(email));

/**
 * True when `user` is the app's ambient anonymous guest session rather than
 * a real (email/password) admin login. Every visitor is signed in this way
 * at boot (see initializeDefaultUser) purely to satisfy the Realtime
 * Database rules' `auth != null`; it carries no identity and no special
 * privilege of its own, unlike the old shared-password account this
 * replaced.
 * @param {import('firebase/auth').User|null|undefined} user
 * @returns {boolean}
 */
export const isAnonymousUser = (user) => !!user?.isAnonymous;

/**
 * The email of whoever is signed in to Firebase right now, or '' if nobody
 * is.
 *
 * Read at call time rather than threaded down as a prop, for the same reason
 * resolveAdminActor in auditLogUtil.js is: otherwise every screen that needs
 * to know who is acting has to receive it from AdminPage through components
 * that have no other use for it.
 *
 * @returns {string}
 */
export const currentAdminEmail = () => auth?.currentUser?.email || '';

/**
 * Whether two emails refer to the same account, case/whitespace-insensitively.
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
export const isSameAccount = (a, b) => {
  const left = normalizeEmail(a);
  return !!left && left === normalizeEmail(b);
};

// ---- Admin idle session ----
// Firebase keeps an admin signed in indefinitely once they authenticate,
// which is wrong for a shared back-office machine. So the dashboard tracks
// its own session record — which account logged in, and when they last
// interacted — independently of Firebase's persistence: a refresh resumes
// the session, an idle tab doesn't.
//
// The account has to be stored alongside the timestamp because the Firebase
// user alone can't answer "did an admin log in here?" — every visitor is
// silently signed in as the shared system account, and that same account is
// also a valid admin login. Storing the email is what lets both cases be
// told apart (and is why refreshing used to bounce the super-admin, but not
// other admins, back to the login form).
const ADMIN_SESSION_KEY = 'jt-admin-session';
// Superseded by ADMIN_SESSION_KEY; removed on write/clear so old records
// can't linger in a browser that's already been used with the previous build.
const LEGACY_ADMIN_ACTIVITY_KEY = 'jt-admin-last-active';
// ADMIN_IDLE_TIMEOUT_MS now lives in constants/bookingConst.js — the login
// card and the Config -> System description both quote it, so it belongs
// with the other shared values rather than here.

/**
 * Reads the raw stored record without applying the idle timeout.
 * @returns {{email: string, lastActive: number}|null}
 */
const readAdminSession = () => {
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const email = normalizeEmail(parsed?.email);
    const lastActive = Number(parsed?.lastActive) || 0;
    if (!email || !lastActive) return null;
    return { email, lastActive };
  } catch {
    // Storage unavailable (private mode) or a malformed record — treated
    // as "no session", which is the safe failure for an admin screen.
    return null;
  }
};

/**
 * Persists the record. Silently gives up when storage is unavailable; the
 * session then just won't survive a refresh.
 * @param {{email: string, lastActive: number}} session
 */
const writeAdminSession = (session) => {
  try {
    localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
    localStorage.removeItem(LEGACY_ADMIN_ACTIVITY_KEY);
  } catch {
    // Ignored — see readAdminSession.
  }
};

/**
 * Opens an admin session for the given account. Called once, on a
 * successful login; starts the idle window.
 * @param {string} email Account that just authenticated.
 */
export const startAdminSession = (email) => {
  const normalized = normalizeEmail(email);
  if (!normalized) return;
  writeAdminSession({ email: normalized, lastActive: Date.now() });
};

/**
 * Records "the admin is still here". No-op when there's no session to
 * extend, so background activity can't resurrect an expired one.
 */
export const touchAdminActivity = () => {
  const session = readAdminSession();
  if (!session) return;
  writeAdminSession({ ...session, lastActive: Date.now() });
};

/** Forgets the session entirely, so the next load requires a fresh login. */
export const clearAdminActivity = () => {
  try {
    localStorage.removeItem(ADMIN_SESSION_KEY);
    localStorage.removeItem(LEGACY_ADMIN_ACTIVITY_KEY);
  } catch {
    // Ignored, same reason as above.
  }
};

/**
 * Whether the admin has been idle past the timeout. Treats a missing
 * record as expired, so a Firebase session left over from before this
 * feature existed can't silently grant access.
 *
 * The idle window itself is optional (Config -> System): a club running the
 * dashboard on its own front-desk machine can turn it off and stay signed in
 * until someone logs out by hand. Turning it off never removes the session
 * RECORD — only the expiry check — so re-enabling it starts the clock from
 * the admin's last real interaction rather than logging them straight out.
 *
 * Defaults to enabled: a caller that forgets to pass the setting gets the
 * safe behaviour, not an admin session that never ends.
 *
 * @param {boolean} [idleTimeoutEnabled=true]
 * @returns {boolean}
 */
export const isAdminSessionExpired = (idleTimeoutEnabled = true) => {
  const session = readAdminSession();
  if (!session) return true;
  if (!idleTimeoutEnabled) return false;
  return (Date.now() - session.lastActive) > ADMIN_IDLE_TIMEOUT_MS;
};

/**
 * The admin session to resume, or null when there isn't a live one. Use
 * this rather than inspecting the Firebase user: it's the only thing that
 * distinguishes a deliberate admin login from the app's shared background
 * session, and it applies the idle timeout.
 * @param {boolean} [idleTimeoutEnabled=true] See isAdminSessionExpired.
 * @returns {{email: string, lastActive: number}|null}
 */
export const getAdminSession = (idleTimeoutEnabled = true) => {
  const session = readAdminSession();
  if (!session) return null;
  return isAdminSessionExpired(idleTimeoutEnabled) ? null : session;
};

/**
 * Resolves with the user Firebase restored from local persistence, or null if
 * there isn't one.
 *
 * Waits for Firebase to FINISH restoring (authStateReady) instead of racing it
 * against a short timer. On boot Firebase does not just read the stored user —
 * it reloads it from the server, refreshing the ID token first if it has
 * expired. With App Check enforced, every one of those calls waits on a
 * reCAPTCHA Enterprise token, so a still-valid admin login routinely takes
 * longer than the old 3-second cut-off. Hitting that cut-off fell through to
 * signInAnonymously, which REPLACED the admin with a fresh anonymous user —
 * that is what signed admins out on every reload, even with "Auto log out
 * idle admins" switched off.
 *
 * The timer never protected against a stall anyway: signInAnonymously awaits
 * the same initialisation internally, so a stuck IndexedDB read would hang it
 * too. AuthGuard's LOADING_TIMEOUT_MS is the real stall guard.
 *
 * @returns {Promise<import('firebase/auth').User|null>}
 */
const getRestoredSession = async () => {
  await auth.authStateReady();
  return auth.currentUser;
};

/**
 * Ensures there's a signed-in Firebase user before the app renders (see
 * AuthGuard.jsx), since the Realtime Database rules gate reads/writes on
 * `auth != null`.
 *
 * This used to sign every visitor into one shared email/password account
 * with its credentials hard-coded in this file — which meant `auth != null`
 * was satisfied by anyone who could read the deployed JS bundle, not just
 * someone who loaded the actual app. It now uses real anonymous auth
 * instead: no credential exists to leak, and each guest gets their own
 * anonymous identity rather than sharing one account (which was also,
 * coincidentally, a CONFIG_ADMIN_EMAILS super-admin).
 *
 * Reuses a persisted session when there is one — this is also what resumes
 * a real admin's email/password login across a refresh, since Firebase
 * persists both the same way — and only calls signInAnonymously when there
 * is nothing to resume. It must never run while a restore is still in
 * flight, or the anonymous sign-in overwrites the admin (see
 * getRestoredSession).
 *
 * Requires the "Anonymous" sign-in provider to be enabled for this project
 * under Firebase Console -> Authentication -> Sign-in method — without it
 * this rejects with `auth/admin-restricted-operation` /
 * `auth/operation-not-allowed` and the app never signs in.
 *
 * @returns {Promise<import('firebase/auth').User>}
 */
export const initializeDefaultUser = async () => {
  const restored = await getRestoredSession();
  if (restored) return restored;

  const userCredential = await signInAnonymously(auth);
  return userCredential.user;
};
